<template>
  <modal
    :name="name"
    :width="520"
    :height="'auto'"
    :scrollable="true"
    :adaptive="true"
    :click-to-close="false"
    :reset="true"
    class="sales-leave-request-modal modal-not-overflow"
    draggable=".app-modal-header__copy"
  >
    <section class="sales-leave-request-modal__content" aria-labelledby="sales-leave-request-title">
      <app-modal-header design="new" title="New leave request" title-id="sales-leave-request-title" @close="close" />
      <form class="sales-leave-request-modal__body" @submit.prevent="submit">
        <app-form-group v-if="managed" design="new" label="Employee" required>
          <app-select
            v-model="form.employee"
            design="new"
            :options="employeeOptions"
            :clearable="false"
            label="full_name"
            placeholder="Employee"
            :error="$v.form.employee.$error"
            @search="onEmployeeSearch"
          />
          <template #error>
            <div v-if="$v.form.employee.$dirty && !$v.form.employee.required">Required field</div>
          </template>
        </app-form-group>
        <app-form-group design="new" label="Leave type" required>
          <app-select
            v-model="form.type"
            design="new"
            :options="types"
            :clearable="false"
            placeholder="Leave type"
            :error="$v.form.type.$error"
            @input="onTypeChange"
          />
          <template #error>
            <div v-if="$v.form.type.$dirty && !$v.form.type.required">Required field</div>
          </template>
        </app-form-group>
        <div class="sales-leave-hint" v-if="startHint">{{ startHint }}</div>
        <app-form-group design="new" label="From" required>
          <app-date-picker
            v-model="form.from_date"
            design="new"
            placeholder="From"
            :disabled-date="isFromDisabled"
            :error="$v.form.from_date.$error"
          />
          <template #error>
            <div v-if="$v.form.from_date.$dirty && !$v.form.from_date.required">Required field</div>
          </template>
        </app-form-group>
        <app-form-group design="new" label="To" required>
          <app-date-picker
            v-model="form.to_date"
            design="new"
            placeholder="To"
            :disabled-date="isToDisabled"
            :error="$v.form.to_date.$error"
          />
          <template #error>
            <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.required">Required field</div>
          </template>
        </app-form-group>
        <div v-if="days" class="sales-leave-hint">{{ days }} calendar days (both dates included)</div>
        <app-form-group design="new" label="Reason" :required="reasonRequired">
          <app-textarea
            v-model="form.reason"
            design="new"
            placeholder="Reason"
            :error="$v.form.reason.$error"
          />
          <template #error>
            <div v-if="$v.form.reason.$dirty && !$v.form.reason.reasonPresent">Required field</div>
          </template>
        </app-form-group>
        <app-form-group design="new" label="Supporting document (optional)">
          <app-uploader
            v-model="form.proof"
            design="new"
            class="custom-uploader--flex"
            form-data-formats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
          />
        </app-form-group>
        <sales-leave-annual :annual="annual" />
        <div v-if="localError" class="sales-leave-error">{{ localError }}</div>
        <div v-if="submitError" class="sales-leave-error" role="alert">{{ submitError }}</div>
        <footer class="sales-leave-request-modal__footer">
          <app-button design="new" theme="dark" type="button" :disabled="submitting" @click.native="close">Cancel</app-button>
          <app-button ref="submit" design="new" theme="success" :disabled="submitting" type="submit">Send request</app-button>
        </footer>
      </form>
    </section>
  </modal>
</template>

<script>
import debounce from 'lodash/debounce'
import { required, requiredIf } from 'vuelidate/lib/validators'
import {
  getEmployeesListSearch,
  getEmployeesSalesLeaveTypes,
  postEmployeesSalesLeaveRequestPreview,
  postEmployeesSalesLeaveRequest,
  postEmployeesSalesLeaveSelfPreview,
  postEmployeesSalesLeaveSelfRequest,
} from '@/http'
import SalesLeaveAnnual from './SalesLeaveAnnual'
import {
  PAID_CODE,
  errorMessage,
  inclusiveDays,
  newRequestStartRange,
  validateNewRequest,
} from './salesLeave'

const emptyForm = () => ({
  employee: null,
  type: null,
  from_date: null,
  to_date: null,
  reason: '',
  proof: null
})

export default {
  name: 'SalesLeaveRequestModal',
  components: { SalesLeaveAnnual },
  props: {
    // 'self' submits for the current user, 'managed' for any non-resident employee
    mode: {
      type: String,
      default: 'self'
    },
    name: {
      type: String,
      default: 'sales-leave-request'
    }
  },
  data() {
    return {
      form: emptyForm(),
      types: [],
      employeeOptions: [],
      annual: null,
      submitting: false,
      submitError: ''
    }
  },
  validations: {
    form: {
      employee: { required: requiredIf(function () { return this.managed }) },
      type: { required },
      from_date: { required },
      to_date: { required },
      reason: {
        reasonPresent(value) {
          return !this.reasonRequired || Boolean((value || '').trim())
        }
      }
    }
  },
  computed: {
    managed() {
      return this.mode === 'managed'
    },
    reasonRequired() {
      return Boolean(this.form.type && this.form.type.reason_required)
    },
    days() {
      const { from_date: from, to_date: to } = this.form
      return from && to && from < to ? inclusiveDays(from, to) : 0
    },
    localError() {
      if (!this.form.from_date || !this.form.to_date) return ''
      return validateNewRequest(this.form)
    },
    startHint() {
      if (!this.form.type) return ''
      if (this.form.type.code === 'sick_leave') return 'Sick leave may start yesterday or today.'
      return 'This leave may start from tomorrow.'
    }
  },
  watch: {
    'form.type'() { this.refreshPreview() },
    'form.from_date'() { this.refreshPreview() },
    'form.to_date'() { this.refreshPreview() },
    'form.employee'() { this.refreshPreview() }
  },
  methods: {
    open() {
      this.form = emptyForm()
      this.annual = null
      this.submitError = ''
      this.$v.form.$reset()
      this.$modal.show(this.name)
      if (!this.types.length) {
        getEmployeesSalesLeaveTypes().then(res => { this.types = res.data })
      }
    },
    close() {
      this.$modal.hide(this.name)
    },
    onTypeChange() {
      this.form.from_date = null
      this.form.to_date = null
    },
    isFromDisabled(date) {
      if (!this.form.type) return false
      const range = newRequestStartRange(this.form.type.code)
      const value = this.dateValue(date)
      return value < range.min || Boolean(range.max && value > range.max)
    },
    isToDisabled(date) {
      return Boolean(this.form.from_date && this.dateValue(date) <= this.form.from_date)
    },
    dateValue(date) {
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    },
    previewRequest(payload, employee) {
      if (this.managed) return postEmployeesSalesLeaveRequestPreview({ ...payload, employee: employee.id })
      return postEmployeesSalesLeaveSelfPreview(payload)
    },
    sendRequest(payload) {
      if (this.managed) return postEmployeesSalesLeaveRequest({ ...payload, employee: this.form.employee.id })
      return postEmployeesSalesLeaveSelfRequest(payload)
    },
    refreshPreview: debounce(function () {
      this.annual = null
      const { type, from_date: from, to_date: to, employee } = this.form
      if (!type || type.code !== PAID_CODE || !from || !to || this.localError) return
      if (this.managed && !employee) return
      const payload = { type: type.code, from_date: from, to_date: to }
      this.previewRequest(payload, employee).then(res => { this.annual = res.data.annual }).catch(() => {})
    }, 300),
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployees(loading, search)
    },
    searchEmployees: debounce(function (loading, search) {
      if (!search) {
        this.employeeOptions = []
        loading(false)
        return
      }
      getEmployeesListSearch({ search, resident: false })
        .then(res => { this.employeeOptions = res.data })
        .finally(() => loading(false))
    }, 350),
    submit() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid || this.localError) return
      this.submitting = true
      this.submitError = ''
      const payload = {
        type: this.form.type.code,
        from_date: this.form.from_date,
        to_date: this.form.to_date,
        reason: (this.form.reason || '').trim(),
        proof: this.form.proof ? this.form.proof.id : null
      }
      this.sendRequest(payload)
        .then(() => {
          this.$notify({ type: 'success', title: 'Leave request sent for approval' })
          this.close()
          this.$emit('created')
        })
        .catch(error => { this.submitError = errorMessage(error) })
        .finally(() => { this.submitting = false })
    }
  }
}
</script>

<style lang="scss">
.sales-leave-request-modal > .vm--modal {
  border-radius: $ui-card-radius !important;
}
</style>

<style lang="scss" scoped>
.sales-leave-request-modal__content {
  border-radius: $ui-card-radius;
  background: $ui-surface;
  color: $ui-text;
  font-family: $ui-font-body;

  :deep(.app-modal-header--new) {
    border-radius: $ui-card-radius $ui-card-radius 0 0;
  }
}

.sales-leave-request-modal__body {
  padding: $ui-space-24;
}

.sales-leave-request-modal__footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: $ui-space-12;
  margin-top: $ui-space-24;
}

.sales-leave-hint {
  margin: 0 0 $ui-space-12;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}

.sales-leave-error {
  margin: $ui-space-8 0;
  color: $ui-red;
  font-size: $ui-font-size-body;
}
</style>
